import React, { useState, useContext, useEffect } from 'react';
import { X, Eye, EyeOff, Mail, Lock, User, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '../App';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const t = {
  en: {
    signIn: 'Sign In',
    register: 'Create Account',
    googleBtn: 'Continue with Google',
    or: 'or',
    name: 'Full Name',
    email: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    createFree: 'Create free account',
    backToSignIn: 'Back to Sign In',
    resetTitle: 'Reset Password',
    resetDesc: 'Enter your email and we\'ll send a reset link.',
    sendReset: 'Send Reset Link',
    resetSent: 'Reset link sent! Check your inbox.',
    namePlaceholder: 'Your full name',
    emailPlaceholder: 'you@example.com',
    passwordPlaceholder: 'At least 8 characters',
    passwordMismatch: 'Passwords do not match',
    passwordShort: 'Password must be at least 8 characters',
    nameShort: 'Name must be at least 2 characters',
    benefits: ['Order history & tracking', 'Saved addresses', 'Wishlist'],
    guestInfo: 'No account? You can still checkout as a guest.',
    continue: 'Continue',
  },
  tr: {
    signIn: 'Giriş Yap',
    register: 'Hesap Oluştur',
    googleBtn: 'Google ile Devam Et',
    or: 'veya',
    name: 'Ad Soyad',
    email: 'E-posta Adresi',
    password: 'Şifre',
    confirmPassword: 'Şifreyi Onayla',
    forgotPassword: 'Şifremi unuttum?',
    noAccount: 'Hesabın yok mu?',
    hasAccount: 'Zaten hesabın var mı?',
    createFree: 'Ücretsiz hesap oluştur',
    backToSignIn: 'Girişe Dön',
    resetTitle: 'Şifre Sıfırla',
    resetDesc: 'E-postanızı girin, sıfırlama bağlantısı göndereceğiz.',
    sendReset: 'Sıfırlama Linki Gönder',
    resetSent: 'Sıfırlama bağlantısı gönderildi! Gelen kutunuzu kontrol edin.',
    namePlaceholder: 'Adınız ve soyadınız',
    emailPlaceholder: 'siz@ornek.com',
    passwordPlaceholder: 'En az 8 karakter',
    passwordMismatch: 'Şifreler eşleşmiyor',
    passwordShort: 'Şifre en az 8 karakter olmalı',
    nameShort: 'Ad en az 2 karakter olmalı',
    benefits: ['Sipariş geçmişi ve takibi', 'Kayıtlı adresler', 'İstek listesi'],
    guestInfo: 'Hesap yok mu? Misafir olarak yine de alışveriş yapabilirsiniz.',
    continue: 'Devam Et',
  },
  el: {
    signIn: 'Σύνδεση',
    register: 'Δημιουργία Λογαριασμού',
    googleBtn: 'Συνέχεια με Google',
    or: 'ή',
    name: 'Ονοματεπώνυμο',
    email: 'Διεύθυνση Email',
    password: 'Κωδικός',
    confirmPassword: 'Επιβεβαίωση Κωδικού',
    forgotPassword: 'Ξεχάσατε τον κωδικό;',
    noAccount: 'Δεν έχετε λογαριασμό;',
    hasAccount: 'Έχετε ήδη λογαριασμό;',
    createFree: 'Δωρεάν εγγραφή',
    backToSignIn: 'Πίσω στη Σύνδεση',
    resetTitle: 'Επαναφορά Κωδικού',
    resetDesc: 'Εισάγετε το email σας και θα σας στείλουμε σύνδεσμο επαναφοράς.',
    sendReset: 'Αποστολή Συνδέσμου',
    resetSent: 'Ο σύνδεσμος εστάλη! Ελέγξτε τα εισερχόμενά σας.',
    namePlaceholder: 'Το πλήρες όνομά σας',
    emailPlaceholder: 'εσείς@παράδειγμα.com',
    passwordPlaceholder: 'Τουλάχιστον 8 χαρακτήρες',
    passwordMismatch: 'Οι κωδικοί δεν ταιριάζουν',
    passwordShort: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες',
    nameShort: 'Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες',
    benefits: ['Ιστορικό παραγγελιών', 'Αποθηκευμένες διευθύνσεις', 'Λίστα επιθυμιών'],
    guestInfo: 'Χωρίς λογαριασμό; Μπορείτε να αγοράσετε ως επισκέπτης.',
    continue: 'Συνέχεια',
  },
  de: {
    signIn: 'Anmelden',
    register: 'Konto Erstellen',
    googleBtn: 'Mit Google Fortfahren',
    or: 'oder',
    name: 'Vollständiger Name',
    email: 'E-Mail-Adresse',
    password: 'Passwort',
    confirmPassword: 'Passwort Bestätigen',
    forgotPassword: 'Passwort vergessen?',
    noAccount: 'Noch kein Konto?',
    hasAccount: 'Bereits ein Konto?',
    createFree: 'Kostenloses Konto erstellen',
    backToSignIn: 'Zurück zur Anmeldung',
    resetTitle: 'Passwort Zurücksetzen',
    resetDesc: 'Geben Sie Ihre E-Mail ein und wir senden Ihnen einen Link.',
    sendReset: 'Reset-Link Senden',
    resetSent: 'Link gesendet! Überprüfen Sie Ihren Posteingang.',
    namePlaceholder: 'Ihr vollständiger Name',
    emailPlaceholder: 'sie@beispiel.com',
    passwordPlaceholder: 'Mindestens 8 Zeichen',
    passwordMismatch: 'Passwörter stimmen nicht überein',
    passwordShort: 'Passwort muss mindestens 8 Zeichen haben',
    nameShort: 'Name muss mindestens 2 Zeichen haben',
    benefits: ['Bestellverlauf & Tracking', 'Gespeicherte Adressen', 'Wunschliste'],
    guestInfo: 'Kein Konto? Sie können trotzdem als Gast kaufen.',
    continue: 'Weiter',
  },
};

const AuthModal = ({ isOpen, onClose, initialTab = 'signin' }) => {
  const { language, setUser, fetchCart, fetchWishlistIds } = useContext(AppContext);
  const lang = t[language] || t.en;

  const [tab, setTab] = useState(initialTab); // 'signin' | 'register' | 'forgot'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });

  useEffect(() => {
    setTab(initialTab);
    setForm({ name: '', email: '', password: '', confirm: '' });
    setResetSent(false);
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.href;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const applySession = async (data) => {
    const isSecure = window.location.protocol === 'https:';
    const cookieString = isSecure
      ? `session_token=${data.session_token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=None; Secure`
      : `session_token=${data.session_token}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
    document.cookie = cookieString;
    localStorage.setItem('session_token', data.session_token);

    // Fetch enriched user with role
    try {
      const meRes = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${data.session_token}` },
      });
      localStorage.setItem('user', JSON.stringify(meRes.data));
      setUser(meRes.data);
    } catch {
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    }

    await fetchCart();
    await fetchWishlistIds();
    onClose();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login/email`, { email: form.email, password: form.password }, { withCredentials: true });
      await applySession(res.data);
      toast.success(language === 'tr' ? 'Hoş geldiniz!' : 'Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error(lang.nameShort); return; }
    if (form.password.length < 8) { toast.error(lang.passwordShort); return; }
    if (form.password !== form.confirm) { toast.error(lang.passwordMismatch); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { name: form.name.trim(), email: form.email, password: form.password }, { withCredentials: true });
      await applySession(res.data);
      toast.success(language === 'tr' ? 'Hesabınız oluşturuldu!' : 'Account created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: form.email }, { withCredentials: true });
      setResetSent(true);
    } catch {
      setResetSent(true); // Don't reveal if email exists
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A951] focus:border-transparent text-sm outline-none transition';
  const btnPrimary = 'w-full py-3 bg-[#1F2D50] hover:bg-[#2a3d6b] text-white font-semibold rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-50';

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[500]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[501] w-full max-w-md mx-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto">

          {/* Header */}
          <div className="bg-gradient-to-r from-[#1F2D50] to-[#2a3d6b] px-6 py-5 flex items-center justify-between">
            <div>
              <p className="text-[#C8A951] text-xs font-semibold uppercase tracking-wider mb-0.5">North Cyprus Philately</p>
              <h2 className="text-white text-xl font-bold">
                {tab === 'forgot' ? lang.resetTitle : tab === 'register' ? lang.register : lang.signIn}
              </h2>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white transition p-1" aria-label="Close">
              <X size={22} />
            </button>
          </div>

          <div className="p-6">

            {/* Tab switcher (sign in / register) */}
            {tab !== 'forgot' && (
              <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
                <button onClick={() => setTab('signin')}
                  className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${tab === 'signin' ? 'bg-white shadow text-[#1F2D50]' : 'text-gray-500 hover:text-gray-700'}`}>
                  {lang.signIn}
                </button>
                <button onClick={() => setTab('register')}
                  className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${tab === 'register' ? 'bg-white shadow text-[#1F2D50]' : 'text-gray-500 hover:text-gray-700'}`}>
                  {lang.register}
                </button>
              </div>
            )}

            {/* Benefits strip (register tab) */}
            {tab === 'register' && (
              <div className="flex gap-3 mb-5 flex-wrap">
                {lang.benefits.map(b => (
                  <span key={b} className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                    <CheckCircle size={12} /> {b}
                  </span>
                ))}
              </div>
            )}

            {/* Google OAuth */}
            {tab !== 'forgot' && (
              <>
                <button onClick={handleGoogleLogin}
                  className="w-full py-3 border-2 border-gray-200 rounded-lg font-semibold text-sm flex items-center justify-center gap-3 hover:bg-gray-50 hover:border-gray-300 transition mb-4">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {lang.googleBtn}
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">{lang.or}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              </>
            )}

            {/* ── Sign In Form ── */}
            {tab === 'signin' && (
              <form onSubmit={handleSignIn} className="space-y-3">
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" required placeholder={lang.emailPlaceholder} value={form.email} onChange={set('email')}
                    className={`${inputCls} pl-10`} autoComplete="email" />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type={showPassword ? 'text' : 'password'} required placeholder={lang.passwordPlaceholder} value={form.password} onChange={set('password')}
                    className={`${inputCls} pl-10 pr-10`} autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setTab('forgot')} className="text-xs text-[#B8941E] hover:underline">
                    {lang.forgotPassword}
                  </button>
                </div>
                <button type="submit" disabled={loading} className={btnPrimary}>
                  {loading ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : null}
                  {lang.signIn}
                </button>
              </form>
            )}

            {/* ── Register Form ── */}
            {tab === 'register' && (
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" required placeholder={lang.namePlaceholder} value={form.name} onChange={set('name')}
                    className={`${inputCls} pl-10`} autoComplete="name" />
                </div>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" required placeholder={lang.emailPlaceholder} value={form.email} onChange={set('email')}
                    className={`${inputCls} pl-10`} autoComplete="email" />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type={showPassword ? 'text' : 'password'} required placeholder={lang.passwordPlaceholder} value={form.password} onChange={set('password')}
                    className={`${inputCls} pl-10 pr-10`} autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type={showConfirm ? 'text' : 'password'} required placeholder={lang.confirmPassword} value={form.confirm} onChange={set('confirm')}
                    className={`${inputCls} pl-10 pr-10`} autoComplete="new-password" />
                  <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {form.confirm && form.password !== form.confirm && (
                  <p className="text-xs text-red-500">{lang.passwordMismatch}</p>
                )}
                <button type="submit" disabled={loading} className={btnPrimary}>
                  {loading ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : null}
                  {lang.register}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  By registering you agree to our <a href="/terms" className="text-[#B8941E] hover:underline">Terms</a> &amp; <a href="/privacy" className="text-[#B8941E] hover:underline">Privacy Policy</a>.
                </p>
              </form>
            )}

            {/* ── Forgot Password ── */}
            {tab === 'forgot' && (
              <div>
                <button onClick={() => setTab('signin')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
                  <ArrowLeft size={14} /> {lang.backToSignIn}
                </button>
                {resetSent ? (
                  <div className="text-center py-4">
                    <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
                    <p className="font-semibold text-green-700">{lang.resetSent}</p>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-3">
                    <p className="text-sm text-gray-600 mb-3">{lang.resetDesc}</p>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="email" required placeholder={lang.emailPlaceholder} value={form.email} onChange={set('email')}
                        className={`${inputCls} pl-10`} />
                    </div>
                    <button type="submit" disabled={loading} className={btnPrimary}>
                      {loading ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : null}
                      {lang.sendReset}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Guest notice */}
            <p className="text-xs text-center text-gray-400 mt-4 border-t pt-4">
              {lang.guestInfo}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthModal;
